import type ApplicationSettingsStore from "./schema/ApplicationSettingsStore";

import { defaultApplicationSettings } from "./applicationSettingsDefinitions";

import { getLocalOverrides } from "../utils/localOverrides";
import type { MailboxInfo } from "owa-client-types";
import { getFeatureFlagsIndexerForMailboxInfo } from "owa-feature-flags";
import type { CustomDataMap } from "owa-analytics-types";
import { getAllowDefaultApplicationSettings } from "../utils/allowDefaultApplicationSettings";
import { getGlobalSettingsAccountIndexer } from "owa-account-source-list-store";

/**
 * STRATIFY DEBT: This package owns mutable per-account settings state and is not a pure utility.
 * It is temporarily classified as utility because application settings are a cross-cutting
 * framework dependency and this classification unblocks migration of lower-layer consumers.
 *
 * Decouple those consumers from the mutable settings implementation by passing resolved settings
 * through their owning layers, then classify this state package as store.
 *
 * Tracking bug:
 * https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/456298
 */
const settingsByAccount: Map<string, ApplicationSettingsStore> = new Map();

function getApplicationSettingsStore(mailboxInfo: MailboxInfo | undefined, accountKey: string): ApplicationSettingsStore {
    const callerStack = new Error("CallerStack").stack ?? "<no stack>";
    const globalAccountKey = getGlobalSettingsAccountIndexer(/*throwIfNotInitialized*/ false);
    return {
        settings: defaultApplicationSettings,
        overrides: getLocalOverrides(mailboxInfo),
        configIDs: undefined,
        lastError: undefined,
        initialized: false,
        report: undefined,
        searchKey: undefined,
        diagHasMailboxInfo: !!mailboxInfo,
        diagAccountKey: accountKey,
        diagGlobalAccountKey: globalAccountKey,
        diagCallerStack: callerStack,
    };
}

export default function getStore(mailboxInfo: MailboxInfo | undefined): ApplicationSettingsStore {
    // When getting the store we want to alert unless we are in OOBE and the default settings are allowed
    const errorIfNoAccountKey = !getAllowDefaultApplicationSettings();
    const key = getFeatureFlagsIndexerForMailboxInfo(mailboxInfo, errorIfNoAccountKey);
    
    let rv = settingsByAccount.get(key);
    
    if (!rv) {
        const store = getApplicationSettingsStore(mailboxInfo, key);
        settingsByAccount.set(key, store);
        rv = store;
    }
    
    return rv;
}

export function areApplicationSettingsInitialized(mailboxInfo: MailboxInfo): boolean | undefined {
    const key = getFeatureFlagsIndexerForMailboxInfo(mailboxInfo);
    const settings = settingsByAccount.get(key);
    return settings?.initialized;
}

// Allow the unit tests to reset the store
export function resetStoreForUnitTests(): void {
    settingsByAccount.clear();
}

// Allow the unit tests to get the values by key
export function getApplicationsSettingsByKeyForUnitTests(key: string): ApplicationSettingsStore | undefined {
    return settingsByAccount.get(key);
}

export function getStoreDiagnosticInfo(): CustomDataMap {
    let initialized: number = 0;
    const keyLengthAndInitState: string[] = [];
    const diagnosticData: CustomDataMap = { settingsStores: settingsByAccount.size, initialized };
    const globalSettingsKey = getFeatureFlagsIndexerForMailboxInfo(undefined);
    
    const diagnostic = Array.from(settingsByAccount).map(([key, value]) => {
        return {
            isGlobalSettings: key === globalSettingsKey,
            initialized: value.initialized,
            keyLength: key.length,
            initWithMailbox: value.diagHasMailboxInfo ? "MbxInit" : "GlobInit",
            initWasGlobal: value.diagAccountKey === globalSettingsKey ? "WasGlobal" : "WasAdditional",
            callerStack: value.diagCallerStack,
        };
    });
    
    const globalInfo = diagnostic.find(info => info.isGlobalSettings);
    if (globalInfo) {
        diagnosticData.hasGlobalSettings = true;
        diagnosticData.isGlobalInitialized = globalInfo.initialized;
    }
    
    for (const value of diagnostic) {
        if (value.initialized) {
            initialized++;
        }
        
        const storeDiagKey = `${value.isGlobalSettings ? "Global" : "Mailbox"}(${value.keyLength})`;
        const initStateInfo = `${value.initialized},${value.initWithMailbox},${value.initWasGlobal}`;
        keyLengthAndInitState.push(`${storeDiagKey}:${initStateInfo}`);
        
        diagnosticData[`stack_${storeDiagKey}`] = value.callerStack;
    }
    
    diagnosticData.initialized = initialized;
    diagnosticData.keyLengthAndInitStates = keyLengthAndInitState.join();
    return diagnosticData;
}
